import { useEffect, useState, RefObject, useRef } from 'react';

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

interface SwipeState {
    isSwiping: boolean;
    direction: Direction;
    deltaX: number;
    deltaY: number;
}

interface SwiperOptions {
    swipeRef: RefObject<HTMLElement> | null;
    onSwipeStart?: (e: TouchEvent) => void;
    onSwipeEnd?: (direction: Direction, e: TouchEvent) => void;
    onSwiping?: (direction: Direction, e: TouchEvent) => void;
}

function useSwipe(options: SwiperOptions): SwipeState;
function useSwipe(swiperRef: RefObject<HTMLElement>): SwipeState;
function useSwipe(options: SwiperOptions | RefObject<HTMLElement>): SwipeState {
    const [isSwiping, setIsSwiping] = useState(false);
    const [direction, setDirection] = useState<Direction>('none');
    const [deltaX, setDeltaX] = useState(0);
    const [deltaY, setDeltaY] = useState(0);
    const swiperRef = (options as RefObject<HTMLElement>).current
        ? (options as RefObject<HTMLElement>)
        : (options as SwiperOptions).swipeRef;
    const swipeOptions = (options as SwiperOptions).swipeRef ? (options as SwiperOptions) : ({} as SwiperOptions);

    const clientX = useRef(0);
    const clientY = useRef(0);
    const isSingleFinger = useRef(true);
    useEffect(() => {
        const handleSwipingStart = (e: TouchEvent) => {
            if (e.touches.length > 1) {
                isSingleFinger.current = false;
                setIsSwiping(false);
                return;
            }
            isSingleFinger.current = true;
            setIsSwiping(true);
            clientX.current = e.touches[0].clientX;
            clientY.current = e.touches[0].clientY;
            swipeOptions.onSwipeStart?.(e);
        };

        const handleSwipingEnd = (e: TouchEvent) => {
            if (!isSingleFinger.current) {
                return;
            }
            swipeOptions.onSwipeEnd?.(direction, e);
            setIsSwiping(false);
            setDirection('none');
        };

        const handleSwiping = (e: TouchEvent) => {
            if (!isSingleFinger.current) {
                return;
            }
            const deltaX = e.touches[0].clientX - clientX.current;
            const deltaY = e.touches[0].clientY - clientY.current;

            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                if (deltaX > 0) {
                    setDirection('right');
                } else {
                    setDirection('left');
                }
            } else {
                if (deltaY > 0) {
                    setDirection('down');
                } else {
                    setDirection('up');
                }
            }
            setDeltaX(deltaX);
            setDeltaY(deltaY);
            swipeOptions.onSwiping?.(direction, e);
        };

        if (swiperRef?.current) {
            swiperRef.current.addEventListener('touchstart', handleSwipingStart);
            swiperRef.current.addEventListener('touchmove', handleSwiping);
            swiperRef.current.addEventListener('touchend', handleSwipingEnd);
        }

        return () => {
            if (swiperRef?.current) {
                swiperRef.current.removeEventListener('touchstart', handleSwipingStart);
                swiperRef.current.removeEventListener('touchmove', handleSwiping);
                swiperRef.current.removeEventListener('touchend', handleSwipingEnd);
            }
        };
    }, [swiperRef, swipeOptions]);

    return {
        isSwiping,
        direction,
        deltaX,
        deltaY,
    };
}

export default useSwipe;
